import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { ModalButtonType, type IModalDialogContent, type ModalDialogOptions, type ModalWindow } from '@components/Modal';
import { PromiseCompletion } from '@classes/PromiseCompletion';
import { ToggleFormButton } from '@components/ToggleFormButton';
import apiService from '@services/apiService';
import ChatToolsTab from '@components/ChatSettings/ChatToolsTab';
import { ImgChevron } from '@images/ImgChevron';

export type SkillDto = {
  id: string;
  name: string;
  description: string;
  systemPrompt: string;
  tools: string[];
};

export type SkillEditDialogProps = {
  skill: SkillDto | null; // null = create new
};

@observer
export default class SkillEditDialog extends React.Component<SkillEditDialogProps> implements IModalDialogContent<SkillDto | null> {
  @observable private _name: string;
  @observable private _description: string;
  @observable private _systemPrompt: string;
  @observable private _selectedTools: string[];
  @observable private _allTools: boolean;
  @observable private _toolsExpanded: boolean = false;

  @observable private _error: string | null = null;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _toolsLoader: PromiseCompletion = new PromiseCompletion();
  // Copy from props in constructor — never read this.props in reactive contexts
  private _isEditing: boolean;
  private _skillId: string | null;

  constructor(props: SkillEditDialogProps) {
    super(props);
    this._isEditing = !!props.skill;
    this._skillId = props.skill?.id ?? null;
    this._name = props.skill?.name ?? '';
    this._description = props.skill?.description ?? '';
    this._systemPrompt = props.skill?.systemPrompt ?? '';
    this._allTools = !props.skill || (props.skill.tools ?? []).length === 0;
    this._selectedTools = props.skill?.tools ?? [];
    makeObservable(this);
  }

  public getModalOptions(window: ModalWindow<SkillDto | null>): ModalDialogOptions<SkillDto | null> {
    return {
      title: this._isEditing ? 'Edit Skill' : 'New Skill',
      loader: this._loader,
      buttons: [
        {
          title: this._isEditing ? 'Save' : 'Create',
          type: ModalButtonType.Save,
          onClick: async () => {
            if (!this._name.trim()) {
              this._error = 'Skill name is required.';
              return;
            }
            this._error = null;
            const payload = {
              name: this._name.trim(),
              description: this._description.trim(),
              systemPrompt: this._systemPrompt.trim(),
              tools: this._allTools ? [] : this._selectedTools,
            };
            try {
              let result: SkillDto;
              if (this._isEditing && this._skillId) {
                result = await apiService.put<SkillDto>(`/api/skills/${this._skillId}`, payload, { loader: this._loader });
              } else {
                result = await apiService.post<SkillDto>('/api/skills', payload, { loader: this._loader });
              }
              window.close(ModalButtonType.Save, result);
            } catch (err) {
              this._error = err instanceof Error ? err.message : 'Save failed.';
            }
          },
        },
        {
          title: 'Cancel',
          type: ModalButtonType.Cancel,
          onClick: () => window.close(ModalButtonType.Cancel, null),
        },
      ],
    };
  }

  render() {
    return (
      <div className="skill-edit-form">
        {this._error && <div className="skill-edit-error">{this._error}</div>}

        <div className="skill-edit-field">
          <label className="skill-edit-label">Name *</label>
          <input
            className="skill-edit-input"
            value={this._name}
            onChange={action((e: React.ChangeEvent<HTMLInputElement>) => { this._name = e.target.value; })}
            placeholder="e.g. Code Reviewer"
            autoFocus
          />
        </div>

        <div className="skill-edit-field">
          <label className="skill-edit-label">Description</label>
          <input
            className="skill-edit-input"
            value={this._description}
            onChange={action((e: React.ChangeEvent<HTMLInputElement>) => { this._description = e.target.value; })}
            placeholder="Short description of what this skill does"
          />
        </div>

        <div className="skill-edit-field">
          <label className="skill-edit-label">System Prompt</label>
          <textarea
            className="skill-edit-textarea"
            value={this._systemPrompt}
            onChange={action((e: React.ChangeEvent<HTMLTextAreaElement>) => { this._systemPrompt = e.target.value; })}
            placeholder="Instructions for the sub-agent (e.g. You are an expert code reviewer...)"
            rows={5}
          />
        </div>

        <div className="skill-edit-field">
          <div
            className="skill-tools-header"
            onClick={action(() => { this._toolsExpanded = !this._toolsExpanded; })}
          >
            <ImgChevron
              size={16}
              className={`skill-tools-chevron${this._toolsExpanded ? ' expanded' : ''}`}
            />
            <span className="skill-edit-label" style={{ margin: 0 }}>Tools</span>
            <span className="skill-edit-label-hint">
              {this._allTools ? ' — all tools' : ` — ${this._selectedTools.length} selected`}
            </span>
          </div>
          <div className={`skill-tools-wrap${this._toolsExpanded ? ' expanded' : ''}`}>
            <div className="skill-tools-body">
              <div className="skill-all-tools-row">
                <ToggleFormButton
                  label="All tools (current and future)"
                  checked={this._allTools}
                  onChange={action((checked: boolean) => { this._allTools = checked; })}
                />
              </div>
              {!this._allTools && (
                <ChatToolsTab
                  tools={this._selectedTools.length === 0 ? null : this._selectedTools}
                  loader={this._toolsLoader}
                  onChange={action((tools: string[]) => { this._selectedTools = tools; })}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
